import type { ReactNode } from 'react';

import { cx } from './cx';

export type ColumnAlign = 'left' | 'center' | 'right';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  align?: ColumnAlign;
  render?: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Stable row identity; falls back to the array index. */
  rowKey?: (row: T) => string;
  emptyMessage?: string;
}

function defaultCell<T>(row: T, key: string): ReactNode {
  const value = (row as Record<string, unknown>)[key];

  if (value === null || value === undefined) {
    return <span className="rfp-table__null">—</span>;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return null;
}

/** Data table on a white surface card: muted mono header row, hover rows. */
export function DataTable<T>({ columns, rows, rowKey, emptyMessage }: DataTableProps<T>) {
  return (
    <div className="rfp-table-card">
      <table className="rfp-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                className={cx(column.align && `rfp-table__cell--${column.align}`)}
                key={column.key}
                scope="col"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="rfp-table__empty" colSpan={columns.length}>
                {emptyMessage ?? 'No records yet'}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={rowKey ? rowKey(row) : index}>
                {columns.map((column) => (
                  <td
                    className={cx(column.align && `rfp-table__cell--${column.align}`)}
                    key={column.key}
                  >
                    {column.render ? column.render(row) : defaultCell(row, column.key)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
